import React, { useCallback, useState } from 'react';
import { AppButton, Box, FormProvider, Preloader, Text } from '@common-fe/common-fe';

import { WarnModal } from '@/components/elements';
import Modal from '@/components/elements/AutoBlockedModal/Modal';

import useUpdateEmployeeAddressQuery from '../queries/useUpdateEmployeeAddress.query';

import EditAddressFormFields from './EditAddressFormFields';
import { EditAddressFormData, useEditAddressForm } from './useEditAddressForm';

interface AddressData {
  id?: string;
  street?: string;
  street_2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  address_type?: string;
}

interface Props {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  address?: AddressData;
}

const EditEmployeeAddressModal: React.FC<Props> = ({
  visible,
  onClose,
  onSuccess,
  address,
}) => {
  const { updateAddress, isLoading } = useUpdateEmployeeAddressQuery();
  const [errorModal, setErrorModal] = useState(false);

  const methods = useEditAddressForm({
    defaultValues: {
      street: address?.street || '',
      street_2: address?.street_2 || '',
      city: address?.city || '',
      state: address?.state || '',
      zipCode: address?.zipCode || '',
    },
  });

  const handleSave = useCallback(async (values: EditAddressFormData) => {
    try {
      await updateAddress({
        line1: values.street,
        line2: values.street_2 || '',
        city: values.city,
        state: values.state,
        zipcode: values.zipCode,
        country_code: 'US',
        address_type: address?.address_type || 'UNKNOWN',
      });
      onSuccess();
    } catch {
      setErrorModal(true);
    }
  }, [updateAddress, address, onSuccess]);

  return (
    <>
      <Modal
        visible={visible}
        onSetVisible={onClose}
        data-testid="edit-employee-address-modal"
      >
        <Box direction="column">
          <Box direction="row" align="center" justify="center" pad={{ bottom: 'spacing24' }}>
            <Text size="2xl" color="textTitle" weight="bold">Edit Address</Text>
          </Box>
          <Box pad="l" background="module" round="moduleRound">
            <Box
              data-testid="edit-employee-address-form"
              background="canvas"
              round="container1Round"
            >
              <FormProvider {...methods}>
                <EditAddressFormFields />
              </FormProvider>
            </Box>
          </Box>
          <Box
            direction="row"
            align="center"
            justify="end"
            pad={{ top: 'l' }}
          >
            <AppButton buttonType="secondary" width="180px" onClick={onClose}>
              Cancel
            </AppButton>
            <Box margin={{ left: 's' }}>
              <AppButton
                width="180px"
                onClick={methods.handleSubmit(handleSave)}
                disabled={isLoading}
              >
                {isLoading ? <Preloader color="white" /> : <Text>Save</Text>}
              </AppButton>
            </Box>
          </Box>
        </Box>
      </Modal>
      <WarnModal
        testId="edit-employee-address-error-modal"
        visible={errorModal}
        onSetVisible={setErrorModal}
        header="The server encountered an error processing the request"
        helptext="It doesn't appear to have affected your data, but we cannot update the Address with the latest records. Our technical staff have been automatically notified and will be looking into this with the utmost urgency."
        buttonText="Close"
        onSubmit={methods.handleSubmit(handleSave)}
        submitButtonText="Try again"
      />
    </>
  );
};

export default EditEmployeeAddressModal;
